import { FC, useMemo } from 'react';

import { WORKFLOW_OBJECT_FIELD_KEY_MAPPINGS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateWorkflowEditor/constants';
import Accordion from 'dashboard/components/Accordion';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  filterUsableObjects,
  getTokensToDisplayValue,
  snakify,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { BaseTrigger, BaseTriggerParams, Trigger } from 'dashboard/models/trigger';
import { VERIFICATION_TEMPLATE_FIELDS } from 'dashboard/models/verification';
import { WorkflowError, WorkflowFieldKey } from 'dashboard/models/workflow';
import { WorkflowActionCreateDocumentGovernmentIdV1 } from 'dashboard/models/workflow-version';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

interface ParamsCreateDocumentGovernmentIdV1Props extends ParamsBaseProps {
  verificationTemplates: {
    name: string;
    token: string;
    type: string;
  }[];
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionCreateDocumentGovernmentIdV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParam: WorkflowActionCreateDocumentGovernmentIdV1['params']) => void;
}

const ParamsCreateDocumentGovernmentIdV1: FC<ParamsCreateDocumentGovernmentIdV1Props> = ({
  verificationTemplates,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  step,
  updateStep,
  updateActionParams,
}) => {
  const [showEditPanel, { disable: closeEditPanel, toggle: toggleEditPanel }] = useToggle(false);
  const hasVerificationTemplateView = usePermission(Permission.VerificationTemplateView);
  const basePath = useMemo(() => {
    switch (trigger?.type) {
      case ApiModel.TriggerApi:
        return [BaseTriggerParams, WorkflowFieldKey.Fields].join('.');
      case ApiModel.TriggerAccount:
      case ApiModel.TriggerCase:
        return [BaseTriggerParams].join('.');
      case ApiModel.TriggerEvent:
      case ApiModel.TriggerSchedule:
      case ApiModel.TriggerRecurring:
      case ApiModel.TriggerDirect:
      default:
        return [BaseTrigger, WorkflowFieldKey.Fields].join('.');
    }
  }, [trigger]);
  const objectOptions = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);
  const isTemplateAvailable = useMemo(() => {
    return (
      !params['verification-template-id'] ||
      verificationTemplates.map((vt) => vt.token).includes(params['verification-template-id'])
    );
  }, [params, verificationTemplates]);
  const verificationTemplateOptions = verificationTemplates
    .filter(
      (verificationTemplate) =>
        verificationTemplate.type === ApiModel.VerificationTemplateGovernmentId,
    )
    .map((verificationTemplate) => ({
      label: verificationTemplate.name,
      value: verificationTemplate.token,
      type: verificationTemplate.type,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const handleVerificationTemplateChange = (value: string) => {
    const fields = VERIFICATION_TEMPLATE_FIELDS[ApiModel.VerificationTemplateGovernmentId];
    updateActionParams({
      ...params,
      'verification-template-id': value,
      'verification-fields': fields.reduce<Record<string, string>>((acc, field) => {
        const fieldKey =
          trigger?.type === ApiModel.TriggerDirect
            ? (WORKFLOW_OBJECT_FIELD_KEY_MAPPINGS[field] ?? snakify(field))
            : snakify(field);
        return {
          ...acc,
          [field]: [basePath, fieldKey].join('.'),
        };
      }, {}),
    });
  };
  const handleEditFieldMappings = (value: Record<string, string>) => {
    updateActionParams({
      ...params,
      'verification-fields': value || {},
    });
  };
  const handleObjectsChange = (value: string[]) => {
    updateActionParams({
      ...params,
      objects: value,
    });
  };

  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError(errors);
  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Verification Template'
        htmlFor='params-create-document-gov-id__verification-template-input'
        helpText={
          hasVerificationTemplateView
            ? isTemplateAvailable
              ? undefined
              : 'The current verification template is not available to you at this time.'
            : 'Your role does not have permissions to view verification templates.'
        }
      />
      <Select
        id='params-create-document-gov-id__verification-template-input'
        value={hasVerificationTemplateView ? params['verification-template-id'] : undefined}
        onValueChange={handleVerificationTemplateChange}
        disabled={!editable || !hasVerificationTemplateView}
        placeholder={
          hasVerificationTemplateView
            ? isTemplateAvailable
              ? undefined
              : 'UNAVAILABLE'
            : 'RESTRICTED'
        }
        hasError={hasErrorFor('verification-template-id')}
      >
        {verificationTemplateOptions.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.label} - {option.value}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('verification-template-id')}
      <Spacer height={2} />
      {isTemplateAvailable && (
        <Accordion defaultExpanded title='Verification Fields'>
          {renderErrorMessageFor('verification-fields')}
          <Spacer height={1} />
          {showEditPanel && (
            <FieldMappingsPanel
              title='Verification Fields'
              usableObjects={usableObjects}
              fieldMappings={params['verification-fields'] ?? {}}
              onOutsideClick={closeEditPanel}
              onUpdateFieldMappings={handleEditFieldMappings}
            />
          )}
          <FieldsList
            fields={params['verification-fields']}
            fieldKey='verification-fields'
            errors={errors}
            editable={editable && params['verification-template-id'] != null}
            placeholderEmptyText={
              params['verification-template-id'] != null
                ? 'No fields selected'
                : 'Please select a verification template'
            }
            tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
            toggleEditPanel={toggleEditPanel}
          />
        </Accordion>
      )}
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <ParamLabel
          labelText='Objects'
          htmlFor='params-create-document-gov-id__objects-input'
          id='params-create-document-gov-id__objects-input'
        />
        <ReactSelect
          aria-labelledby='params-create-document-gov-id__objects-input'
          id='params-create-document-gov-id__objects-input'
          onChange={handleObjectsChange}
          value={params.objects}
          options={objectOptions}
          disabled={!editable}
          closeMenuOnSelect={false}
          blurInputOnSelect={false}
          isMulti
        />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsCreateDocumentGovernmentIdV1;
